import React, { useMemo } from 'react';
import {
  BarsArrowUpIcon,
  BanknotesIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import {
  SegmentedToggle,
  EmptyState,
  TWSelect,
} from '../../../components/ui';
import { formatCurrency } from '../../../utils/baseUtils';
import NoItems from '../../../assets/no-items.svg';

const cn = (...c) => c.filter(Boolean).join(' ');

// Only date ranges that make sense for "trending items" — no Today/Yesterday
const TOP10_DATE_OPTIONS = [
  { value: 'last7Days', label: 'Last 7 Days' },
  { value: 'last14Days', label: 'Last 14 Days' },
  { value: 'last30Days', label: 'Last 30 Days' },
  { value: 'last60Days', label: 'Last 60 Days' },
];

const TopItemsPanel = ({
  items,
  top10Type,
  setTop10Type,
  selectedTop10DateRange,
  onTop10DateRangeSelectChange,
}) => {
  const maxValue = useMemo(() => {
    const key = top10Type === 'Sales' ? 'total' : 'qty';
    return items.reduce((acc, item) => Math.max(acc, item[key] || 0), 0);
  }, [items, top10Type]);

  const isEmpty = items.length === 0;

  return (
    <section className="reveal flex h-full flex-col overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/70">
      <header className="flex flex-col gap-3 px-5 py-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <BarsArrowUpIcon
              className="h-6 w-6 flex-shrink-0 text-slate-900 dark:text-slate-100"
              aria-hidden
            />
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
              Top Ten Popular Items
            </h3>
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            See what's trending at your restaurant.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className={onTop10DateRangeSelectChange ? 'min-w-0 flex-1' : ''}>
            <SegmentedToggle
              block={!!onTop10DateRangeSelectChange}
              ariaLabel="Top items metric"
              value={top10Type}
              onChange={setTop10Type}
              options={[
                { value: 'Sales', label: 'Sales', icon: BanknotesIcon },
                { value: 'Items', label: 'Items', icon: ShoppingBagIcon },
              ]}
            />
          </div>
          {onTop10DateRangeSelectChange && (
            <div className="min-w-0 flex-1">
              <TWSelect
                size="large"
                showSearch={false}
                allowClear={false}
                value={selectedTop10DateRange}
                onChange={onTop10DateRangeSelectChange}
                options={TOP10_DATE_OPTIONS}
              />
            </div>
          )}
        </div>
      </header>

      <div className="flex min-h-[16rem] flex-1 flex-col">
        {isEmpty ? (
          <EmptyState
            variant="plain"
            image={NoItems}
            title="No items yet"
            description="Bestsellers will appear once orders come in."
            className="h-full flex-1"
          />
        ) : (
          <ol className="divide-y divide-slate-100 dark:divide-slate-700/70">
            {items.map((entry, i) => {
              const value = top10Type === 'Sales' ? entry.total : entry.qty;
              const helper =
                top10Type === 'Sales'
                  ? `${entry.qty} orders`
                  : formatCurrency(entry.total);
              const widthPct =
                maxValue > 0 ? Math.max((value / maxValue) * 100, 4) : 0;

              return (
                <li
                  key={`${entry.product?.id || i}-${i}`}
                  className="group px-5 py-3 transition hover:bg-slate-50/70 dark:hover:bg-slate-700/30"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'inline-flex h-7 w-7 flex-none items-center justify-center rounded-full text-[0.7rem] font-bold tabular-nums',
                        i < 3
                          ? 'bg-blue-500/10 text-blue-600 dark:bg-sky-400/10 dark:text-sky-400'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-700/60 dark:text-slate-300',
                      )}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {entry.product.name}
                      </p>
                      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700/60">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all dark:bg-sky-400"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="tabular-nums text-sm font-bold text-slate-900 dark:text-slate-100">
                        {top10Type === 'Sales'
                          ? formatCurrency(entry.total)
                          : `${entry.qty}`}
                      </span>
                      <span className="text-[0.7rem] text-slate-500 dark:text-slate-400">
                        {helper}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
};

export default TopItemsPanel;
